import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import ElevatorRoundedIcon from '@mui/icons-material/ElevatorRounded';
import EngineeringRoundedIcon from '@mui/icons-material/EngineeringRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import RuleRoundedIcon from '@mui/icons-material/RuleRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { useAnalytics } from '~/analytics';
import {
  type AreaLineChartOption,
  echarts,
  firstTooltipParam,
  ReactECharts,
} from '~/components/chart/echarts';
import { liftAPIClientErrorDetail } from '~/components/form/utils';
import {
  KnowledgeContentPromptCard,
  type KnowledgeContentPromptCardData,
  KnowledgeDraftCard,
  type KnowledgeDraftCardData,
  KnowledgeResultsCard,
  type KnowledgeResultsCardData,
  KnowledgeTargetChoiceCard,
  type KnowledgeTargetChoiceCardData,
} from '~/components/knowledge/KnowledgeChatCards';
import { useOttoModeOrClosed } from '~/components/otto/OttoProvider';
import { contractsReviewRulesCreate } from '~/data/generated/endpoints/contracts/contracts';
import { LiftAPIClientError } from '~/data/liftApiMutator';
import { useOpenInvoice } from '~/hooks/useOpenInvoice';
import { type CsvColumn } from '~/utils/exportRows';

import { ChatMarkdown } from './ChatMarkdown';
import { ExportCsvButton } from './ExportCsvButton';

/**
 * Attribution context for rule-suggestion analytics. OttoConversation provides
 * the session id and the invoice the chat was opened from (when started via the
 * post-finalize nudge), so the chat-side rule_suggestion_* events can tie back
 * to the originating invoice review. Empty when rendered outside a conversation.
 */
export const ChatAttributionContext = createContext<{
  sessionId?: string;
  invoiceId?: string | null;
}>({});

// ─── Data types ──────────────────────────────────────────────────────────────

export type InvoiceCardData = {
  id: string;
  invoiceNumber: string;
  vendor: string;
  property: string;
  amount: number;
  status: 'approved' | 'rejected' | 'pending' | 'in_review';
  date: string;
  kind?: 'invoice' | 'proposal' | 'credit';
};

export type PropertyCardData = {
  id: string;
  name: string;
  address: string;
  unitCount: number;
  portfolio?: string;
  callbackRate?: number;
  maintenanceCompletion?: number;
};

export type VendorCardData = {
  id: string;
  name: string;
  propertyCount?: number;
  unitCount?: number;
  /** Callback tickets the vendor logged in the tool's window: a count, not a rate. */
  callbacks?: number;
  /** The emergency subset of `callbacks`. */
  emergencyCallbacks?: number;
  /** Maintenance visit tickets the vendor logged in the window. */
  maintenanceVisits?: number;
  /** Contracts the vendor holds at properties in scope. */
  contractCount?: number;
  /**
   * Averaged per-contract rates. render_vendor_cards no longer sends these; kept so a
   * session saved to localStorage before that change still renders its old cards.
   */
  callbackRate?: number;
  maintenanceCompletion?: number;
  /** Whole-number percent 0-100 of the vendor's callbacks answered on time. */
  onTimeResponse?: number;
  invoiceCount?: number;
  totalSpend?: number;
  avgInvoice?: number;
  trend?: 'up' | 'down' | 'flat';
};

type PerformanceTableRow = {
  label: string;
  values: Record<string, number | string | null>;
};

export type PerformanceTableData = {
  title: string;
  columns: {
    key: string;
    label: string;
    format?: 'percent' | 'number' | 'hours' | 'text';
    highlightBest?: 'low' | 'high';
  }[];
  rows: PerformanceTableRow[];
};

export type KPIGaugeData = {
  label: string;
  // null means "no data" — the metric set may surface gauges for KPIs
  // that the backend couldn't compute for this property (e.g. no
  // callbacks recorded yet). The card must render that state visually
  // distinct from a real 0, not as the string "null".
  value: number | null;
  target: number;
  format: 'percent' | 'number';
  lowerIsBetter?: boolean;
};

export type UnitCardData = {
  id: string;
  unitName: string;
  propertyName: string;
  manufacturer?: string;
  type?: string;
  callbackRate?: number;
  callbackRateTarget?: number;
  maintenanceCompletion?: number;
};

export type TicketCardData = {
  id: string;
  ticketNumber: string;
  title: string;
  property: string;
  status: 'open' | 'closed' | 'in_progress';
  date: string;
  category?: string;
};

type PricingTableRow = {
  label: string;
  values: { vendor: string; amount: number; invoiceCount?: number }[];
};

export type PricingTableData = {
  title: string;
  rows: PricingTableRow[];
  portfolioAvg?: number;
};

type TrendPoint = {
  label: string;
  value: number;
};

export type TrendChartData = {
  title: string;
  points: TrendPoint[];
  unit?: string;
};

export type StatHighlightData = {
  label: string;
  value: string;
  sentiment?: 'positive' | 'negative' | 'neutral';
};

export type ExpiringCertificateCardData = {
  id: string;
  carId?: string | null;
  kind?: string | null;
  expiresOn?: string | null;
  jurisdiction?: string | null;
  propertyName?: string | null;
  unitName?: string | null;
};

export type OpenViolationCardData = {
  id: string;
  carId?: string | null;
  code?: string | null;
  correctionDue?: string | null;
  responsibility?: string | null;
  status: 'open' | 'overdue';
  propertyName?: string | null;
  unitName?: string | null;
};

export type ComplianceCardListData = {
  expiringCertificates?: ExpiringCertificateCardData[];
  openViolations?: OpenViolationCardData[];
};

export type ReviewRuleCategory =
  | 'review-rule'
  | 'included-service'
  | 'excluded-service'
  | 'miscellaneous-charge'
  | 'other-term';

export type SuggestedReviewRule = {
  reviewRule: string;
  category: ReviewRuleCategory;
};

export type RuleSuggestionsCardData = {
  contractId: string;
  suggestedRules: SuggestedReviewRule[];
  conflictingRuleIds: string[];
};

export type ContentBlock =
  | { type: 'text'; content: string }
  | { type: 'rule-suggestions'; data: RuleSuggestionsCardData }
  | { type: 'invoice-cards'; invoices: InvoiceCardData[] }
  | { type: 'property-cards'; properties: PropertyCardData[] }
  | { type: 'vendor-cards'; vendors: VendorCardData[] }
  | { type: 'unit-cards'; units: UnitCardData[] }
  | { type: 'ticket-cards'; tickets: TicketCardData[] }
  | { type: 'pricing-table'; data: PricingTableData }
  | { type: 'performance-table'; data: PerformanceTableData }
  | { type: 'kpi-gauge'; gauges: KPIGaugeData[] }
  | { type: 'trend-chart'; data: TrendChartData }
  | { type: 'stat-highlights'; stats: StatHighlightData[] }
  | { type: 'compliance-cards'; data: ComplianceCardListData }
  | { type: 'knowledge-draft'; data: KnowledgeDraftCardData }
  | { type: 'knowledge-results'; data: KnowledgeResultsCardData }
  | { type: 'knowledge-target-choice'; data: KnowledgeTargetChoiceCardData }
  | { type: 'knowledge-content-prompt'; data: KnowledgeContentPromptCardData };

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
}

// Round to 2 decimal places and strip trailing zeros so "1.1166958041958042"
// renders as "1.12" and integers like "0" stay "0". KPI metrics arrive as
// raw floats (callback rate, emergency callback rate) or pre-scaled 0–100
// percents that may carry small float drift; both need consistent display.
function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return String(value);
  return Number(value.toFixed(2)).toString();
}

function pluralize(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  approved: { bg: '#E8F5E9', text: '#2E7D32' },
  rejected: { bg: '#FFEBEE', text: '#C62828' },
  pending: { bg: '#FFF3E0', text: '#E65100' },
  in_review: { bg: '#E3F2FD', text: '#1565C0' },
  open: { bg: '#FFF3E0', text: '#E65100' },
  closed: { bg: '#E8F5E9', text: '#2E7D32' },
  in_progress: { bg: '#E3F2FD', text: '#1565C0' },
};

function StatusChip({ status }: { status: string }) {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <Chip
      label={status.replace('_', ' ')}
      size="small"
      sx={{
        height: 20,
        fontSize: '0.65rem',
        fontWeight: 600,
        textTransform: 'capitalize',
        bgcolor: colors.bg,
        color: colors.text,
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  );
}

// ─── CSV export columns ──────────────────────────────────────────────────────
//
// Explicit per-block allow-lists. The card DTOs carry `id` for click-through
// navigation; it is deliberately absent here, so adding a field to a card
// can't silently add it to a file a consultant sends to a client.

const INVOICE_CSV_COLUMNS: CsvColumn<InvoiceCardData>[] = [
  { header: 'Invoice #', value: (r) => r.invoiceNumber },
  { header: 'Type', value: (r) => r.kind },
  { header: 'Vendor', value: (r) => r.vendor },
  { header: 'Property', value: (r) => r.property },
  { header: 'Amount', value: (r) => r.amount },
  { header: 'Status', value: (r) => r.status },
  { header: 'Date', value: (r) => r.date },
];

const PROPERTY_CSV_COLUMNS: CsvColumn<PropertyCardData>[] = [
  { header: 'Property', value: (r) => r.name },
  { header: 'Address', value: (r) => r.address },
  { header: 'Portfolio', value: (r) => r.portfolio },
  { header: 'Units', value: (r) => r.unitCount },
  { header: 'Callback Rate', value: (r) => r.callbackRate },
  { header: 'Maintenance Completion', value: (r) => r.maintenanceCompletion },
];

// Counts, plus the one vendor rate that needs no car count. No callback-rate column:
// there is no per-vendor car count to divide by (see render_vendor_cards).
export const VENDOR_CSV_COLUMNS: CsvColumn<VendorCardData>[] = [
  { header: 'Vendor', value: (r) => r.name },
  { header: 'Callbacks', value: (r) => r.callbacks },
  { header: 'Emergency Callbacks', value: (r) => r.emergencyCallbacks },
  { header: 'Maintenance Visits', value: (r) => r.maintenanceVisits },
  { header: 'On-Time Response (%)', value: (r) => r.onTimeResponse },
  { header: 'Contracts', value: (r) => r.contractCount },
];

const UNIT_CSV_COLUMNS: CsvColumn<UnitCardData>[] = [
  { header: 'Unit', value: (r) => r.unitName },
  { header: 'Property', value: (r) => r.propertyName },
  { header: 'Manufacturer', value: (r) => r.manufacturer },
  { header: 'Type', value: (r) => r.type },
  { header: 'Callback Rate', value: (r) => r.callbackRate },
  { header: 'Maintenance Completion', value: (r) => r.maintenanceCompletion },
];

const TREND_CSV_COLUMNS: CsvColumn<TrendChartData['points'][number]>[] = [
  { header: 'Period', value: (r) => r.label },
  { header: 'Value', value: (r) => r.value },
];

const TICKET_CSV_COLUMNS: CsvColumn<TicketCardData>[] = [
  { header: 'Ticket #', value: (r) => r.ticketNumber },
  { header: 'Title', value: (r) => r.title },
  { header: 'Property', value: (r) => r.property },
  { header: 'Category', value: (r) => r.category },
  { header: 'Status', value: (r) => r.status },
  { header: 'Date', value: (r) => r.date },
];

/**
 * Card lists are capped server-side (25 for card tools, top-50 for grouped
 * aggregates), so an export contains what is on screen, not necessarily the
 * whole result set. Stated on the export row rather than left for the user to
 * discover from a short file.
 */
const CARD_LIST_EXPORT_NOTE = 'Exports the rows shown above.';

function ExportRow({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 1,
        mt: 0.5,
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
        {CARD_LIST_EXPORT_NOTE}
      </Typography>
      {children}
    </Box>
  );
}

// ─── Entity cards ────────────────────────────────────────────────────────────

function CardShell({
  children,
  onClick,
  icon,
  accentColor,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  icon: React.ReactNode;
  accentColor: string;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        textAlign: 'left',
        width: '100%',
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${alpha('#000', 0.07)}`,
        background: '#fff',
        transition: 'all 0.15s',
        '&:hover': {
          borderColor: alpha(accentColor, 0.3),
          boxShadow: `0 2px 8px ${alpha(accentColor, 0.1)}`,
          '& .card-arrow': { opacity: 1, transform: 'translateX(0)' },
        },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          background: alpha(accentColor, 0.08),
          color: accentColor,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      <ArrowForwardRoundedIcon
        className="card-arrow"
        sx={{
          fontSize: 16,
          color: alpha('#000', 0.25),
          mt: 0.5,
          opacity: 0,
          transform: 'translateX(-4px)',
          transition: 'all 0.15s',
        }}
      />
    </ButtonBase>
  );
}

function InvoiceCard({ invoice }: { invoice: InvoiceCardData }) {
  const theme = useTheme();
  const openInvoice = useOpenInvoice();
  const isProposal = invoice.kind === 'proposal';
  return (
    <CardShell
      icon={
        isProposal ? (
          <DescriptionRoundedIcon sx={{ fontSize: 18 }} />
        ) : (
          <ReceiptLongRoundedIcon sx={{ fontSize: 18 }} />
        )
      }
      accentColor={isProposal ? theme.palette.warning.main : theme.palette.primary.main}
      onClick={() => openInvoice(invoice.id)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }} noWrap>
          {invoice.invoiceNumber}
        </Typography>
        <StatusChip status={invoice.status} />
      </Box>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', display: 'block', fontSize: '0.72rem' }}
        noWrap
      >
        {invoice.vendor} &middot; {invoice.property}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, color: theme.palette.primary.dark, fontSize: '0.82rem' }}
        >
          {/* A credit is a deduction. */}
          {formatCurrency(invoice.kind === 'credit' ? -invoice.amount : invoice.amount)}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>
          {invoice.date}
        </Typography>
      </Box>
    </CardShell>
  );
}

function PropertyCard({ property }: { property: PropertyCardData }) {
  const theme = useTheme();
  return (
    <CardShell
      icon={<ApartmentRoundedIcon sx={{ fontSize: 18 }} />}
      accentColor="#6A1B9A"
      onClick={() => {
        // In production: navigate to /portfolio/:id
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }} noWrap>
        {property.name}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', display: 'block', fontSize: '0.72rem' }}
        noWrap
      >
        {property.address}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>
          {property.unitCount} unit{property.unitCount !== 1 ? 's' : ''}
          {property.portfolio ? ` · ${property.portfolio}` : ''}
        </Typography>
        {property.callbackRate != null && (
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.68rem',
              fontWeight: 600,
              color:
                property.callbackRate > 4 ? theme.palette.error.main : theme.palette.success.main,
            }}
          >
            {formatNumber(property.callbackRate)} CBR
          </Typography>
        )}
        {property.maintenanceCompletion != null && (
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.68rem',
              fontWeight: 600,
              color:
                property.maintenanceCompletion >= 100
                  ? theme.palette.success.main
                  : theme.palette.warning.main,
            }}
          >
            {formatNumber(property.maintenanceCompletion)}% MC
          </Typography>
        )}
      </Box>
    </CardShell>
  );
}

function VendorCard({ vendor }: { vendor: VendorCardData }) {
  const theme = useTheme();
  return (
    <CardShell
      icon={<EngineeringRoundedIcon sx={{ fontSize: 18 }} />}
      accentColor="#00695C"
      onClick={() => {
        // In production: navigate to contracts or spending filtered by vendor
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }} noWrap>
        {vendor.name}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', display: 'block', fontSize: '0.72rem' }}
      >
        {vendor.propertyCount != null ? `${vendor.propertyCount} properties` : ''}
        {vendor.propertyCount != null && vendor.unitCount != null ? ' · ' : ''}
        {vendor.unitCount != null ? `${vendor.unitCount} units` : ''}
        {!vendor.propertyCount && !vendor.unitCount && vendor.invoiceCount
          ? `${vendor.invoiceCount} invoices`
          : ''}
      </Typography>
      {vendor.callbacks != null && (
        <Typography
          variant="caption"
          sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, mt: 0.5 }}
        >
          {pluralize(vendor.callbacks, 'callback')}
          {vendor.emergencyCallbacks ? ` (${vendor.emergencyCallbacks} emergency)` : ''}
          {vendor.onTimeResponse != null ? ` · ${vendor.onTimeResponse}% on time` : ''}
        </Typography>
      )}
      {(vendor.maintenanceVisits != null || vendor.contractCount != null) && (
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', display: 'block', fontSize: '0.72rem' }}
        >
          {[
            vendor.maintenanceVisits != null
              ? pluralize(vendor.maintenanceVisits, 'maintenance visit')
              : null,
            vendor.contractCount != null ? pluralize(vendor.contractCount, 'contract') : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
      )}
      {(vendor.callbackRate != null || vendor.maintenanceCompletion != null) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          {vendor.callbackRate != null && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color:
                  vendor.callbackRate > 4 ? theme.palette.error.main : theme.palette.success.main,
              }}
            >
              {formatNumber(vendor.callbackRate)} CBR
            </Typography>
          )}
          {vendor.maintenanceCompletion != null && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color:
                  vendor.maintenanceCompletion >= 100
                    ? theme.palette.success.main
                    : theme.palette.warning.main,
              }}
            >
              {formatNumber(vendor.maintenanceCompletion)}% MC
            </Typography>
          )}
        </Box>
      )}
      {vendor.totalSpend != null && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, fontSize: '0.82rem', color: theme.palette.primary.dark }}
          >
            {formatCurrency(vendor.totalSpend)}
          </Typography>
          {vendor.trend && vendor.trend !== 'flat' ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color:
                  vendor.trend === 'up' ? theme.palette.error.main : theme.palette.success.main,
              }}
            >
              {vendor.trend === 'up' ? (
                <TrendingUpRoundedIcon sx={{ fontSize: 14 }} />
              ) : (
                <TrendingDownRoundedIcon sx={{ fontSize: 14 }} />
              )}
            </Box>
          ) : null}
        </Box>
      )}
    </CardShell>
  );
}

function UnitCard({ unit }: { unit: UnitCardData }) {
  const theme = useTheme();
  return (
    <CardShell
      icon={<ElevatorRoundedIcon sx={{ fontSize: 18 }} />}
      accentColor="#4527A0"
      onClick={() => {
        // In production: navigate to property > unit
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }} noWrap>
        {unit.unitName}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', display: 'block', fontSize: '0.72rem' }}
        noWrap
      >
        {unit.propertyName}
        {unit.manufacturer || unit.type
          ? ` · ${[unit.manufacturer, unit.type].filter(Boolean).join(' ')}`
          : null}
      </Typography>
      {(unit.callbackRate != null || unit.maintenanceCompletion != null) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          {unit.callbackRate != null && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color:
                  unit.callbackRateTarget != null && unit.callbackRate > unit.callbackRateTarget
                    ? theme.palette.error.main
                    : theme.palette.success.main,
              }}
            >
              {formatNumber(unit.callbackRate)} CBR
              {unit.callbackRateTarget != null && (
                <Box component="span" sx={{ fontWeight: 400, color: 'text.disabled' }}>
                  {' '}
                  / {formatNumber(unit.callbackRateTarget)}% target
                </Box>
              )}
            </Typography>
          )}
          {unit.maintenanceCompletion != null && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color:
                  unit.maintenanceCompletion >= 100
                    ? theme.palette.success.main
                    : theme.palette.warning.main,
              }}
            >
              {formatNumber(unit.maintenanceCompletion)}% MC
            </Typography>
          )}
        </Box>
      )}
    </CardShell>
  );
}

function TicketCard({ ticket }: { ticket: TicketCardData }) {
  const theme = useTheme();
  return (
    <CardShell
      icon={<ReceiptLongRoundedIcon sx={{ fontSize: 18 }} />}
      accentColor={theme.palette.warning.main}
      onClick={() => {
        // In production: navigate to /tickets filtered
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }} noWrap>
          {ticket.ticketNumber}
        </Typography>
        <StatusChip status={ticket.status} />
      </Box>
      <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.3 }} noWrap>
        {ticket.title}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: 'text.disabled', fontSize: '0.68rem', display: 'block', mt: 0.25 }}
      >
        {ticket.property} &middot; {ticket.date}
        {ticket.category ? ` · ${ticket.category}` : ''}
      </Typography>
    </CardShell>
  );
}

// ─── Card list wrappers ──────────────────────────────────────────────────────

function CardGrid({ children }: { children: React.ReactNode }) {
  // The MUI `sm` breakpoint (~600px) is viewport-based, but Otto's dock
  // panel is only 440px wide — at viewport ≥ sm the grid would otherwise
  // render two squished cards per row inside the dock. Pin to a single
  // column when Otto is docked; fall back to the breakpoint layout for
  // the full Otto panel and for non-Otto surfaces (e.g. the Intelligence
  // route page) where `useOttoModeOrClosed` returns "closed".
  const ottoMode = useOttoModeOrClosed();
  const isDock = ottoMode === 'dock';
  return (
    <Box
      sx={{
        display: 'grid',
        // `minmax(0, 1fr)` rather than `1fr` — CSS Grid's default min track
        // size is `auto`, which lets long content (e.g. a property's full
        // street address) push the track wider than 1fr and clip past the
        // card boundary. Forcing the min to 0 keeps tracks equal-width and
        // lets `noWrap` Typography children truncate with an ellipsis.
        gridTemplateColumns: isDock
          ? 'minmax(0, 1fr)'
          : { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) minmax(0, 1fr)' },
        gap: 1,
        my: 1.5,
      }}
    >
      {children}
    </Box>
  );
}

export function InvoiceCardList({ invoices }: { invoices: InvoiceCardData[] }) {
  return (
    <>
      <CardGrid>
        {invoices.map((inv) => (
          <InvoiceCard key={inv.id} invoice={inv} />
        ))}
      </CardGrid>
      <ExportRow>
        <ExportCsvButton
          rows={invoices}
          columns={INVOICE_CSV_COLUMNS}
          title="Invoices"
          blockType="invoice-cards"
        />
      </ExportRow>
    </>
  );
}

function ExpiringCertificateCard({ certificate }: { certificate: ExpiringCertificateCardData }) {
  const navigate = useNavigate();
  const subtitle = [certificate.propertyName, certificate.unitName].filter(Boolean).join(' · ');
  const onClick = certificate.carId
    ? () => navigate(`/compliance?carId=${certificate.carId}&car_panel=open`)
    : undefined;
  return (
    <CardShell
      icon={<VerifiedRoundedIcon sx={{ fontSize: 18 }} />}
      accentColor="#2E7D32"
      onClick={onClick}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }} noWrap>
        {certificate.kind ?? 'Certificate'}
      </Typography>
      {subtitle ? (
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', display: 'block', fontSize: '0.72rem' }}
          noWrap
        >
          {subtitle}
        </Typography>
      ) : null}
      {certificate.expiresOn ? (
        <Typography
          variant="caption"
          sx={{ color: 'text.disabled', display: 'block', fontSize: '0.68rem' }}
        >
          Expires {certificate.expiresOn}
        </Typography>
      ) : null}
    </CardShell>
  );
}

function OpenViolationCard({ violation }: { violation: OpenViolationCardData }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const subtitle = [violation.propertyName, violation.unitName].filter(Boolean).join(' · ');
  const isOverdue = violation.status === 'overdue';
  // Two accent colors so a glance at the card stack tells the user
  // which items are already past correction due — overdue items get
  // the same warning color the rest of the chat surface uses.
  const accentColor = isOverdue ? theme.palette.error.main : theme.palette.warning.dark;
  const onClick = violation.carId
    ? () => navigate(`/compliance?carId=${violation.carId}&car_panel=open`)
    : undefined;
  return (
    <CardShell
      icon={<GavelRoundedIcon sx={{ fontSize: 18 }} />}
      accentColor={accentColor}
      onClick={onClick}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }} noWrap>
          {violation.code ?? 'Violation'}
        </Typography>
        <Chip
          label={isOverdue ? 'Overdue' : 'Open'}
          size="small"
          sx={{
            height: 16,
            fontSize: '0.62rem',
            fontWeight: 600,
            color: accentColor,
            background: alpha(accentColor, 0.12),
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      </Box>
      {subtitle ? (
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', display: 'block', fontSize: '0.72rem' }}
          noWrap
        >
          {subtitle}
        </Typography>
      ) : null}
      {violation.correctionDue ? (
        <Typography
          variant="caption"
          sx={{ color: 'text.disabled', display: 'block', fontSize: '0.68rem' }}
        >
          Correction due {violation.correctionDue}
        </Typography>
      ) : null}
    </CardShell>
  );
}

export function ComplianceCardList({ data }: { data: ComplianceCardListData }) {
  const certs = data.expiringCertificates ?? [];
  const violations = data.openViolations ?? [];
  if (certs.length === 0 && violations.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', my: 1 }}>
        No compliance items in this window.
      </Typography>
    );
  }
  return (
    <CardGrid>
      {certs.map((cert) => (
        <ExpiringCertificateCard key={`cert-${cert.id}`} certificate={cert} />
      ))}
      {violations.map((viol) => (
        <OpenViolationCard key={`viol-${viol.id}`} violation={viol} />
      ))}
    </CardGrid>
  );
}

function PropertyCardList({ properties }: { properties: PropertyCardData[] }) {
  return (
    <>
      <CardGrid>
        {properties.map((p) => (
          <PropertyCard key={p.id} property={p} />
        ))}
      </CardGrid>
      <ExportRow>
        <ExportCsvButton
          rows={properties}
          columns={PROPERTY_CSV_COLUMNS}
          title="Properties"
          blockType="property-cards"
        />
      </ExportRow>
    </>
  );
}

export function VendorCardList({ vendors }: { vendors: VendorCardData[] }) {
  return (
    <>
      <CardGrid>
        {vendors.map((v) => (
          <VendorCard key={v.id} vendor={v} />
        ))}
      </CardGrid>
      <ExportRow>
        <ExportCsvButton
          rows={vendors}
          columns={VENDOR_CSV_COLUMNS}
          title="Vendors"
          blockType="vendor-cards"
        />
      </ExportRow>
    </>
  );
}

function UnitCardList({ units }: { units: UnitCardData[] }) {
  return (
    <>
      <CardGrid>
        {units.map((u) => (
          <UnitCard key={u.id} unit={u} />
        ))}
      </CardGrid>
      <ExportRow>
        <ExportCsvButton
          rows={units}
          columns={UNIT_CSV_COLUMNS}
          title="Units"
          blockType="unit-cards"
        />
      </ExportRow>
    </>
  );
}

function TicketCardList({ tickets }: { tickets: TicketCardData[] }) {
  return (
    <>
      <CardGrid>
        {tickets.map((t) => (
          <TicketCard key={t.id} ticket={t} />
        ))}
      </CardGrid>
      <ExportRow>
        <ExportCsvButton
          rows={tickets}
          columns={TICKET_CSV_COLUMNS}
          title="Tickets"
          blockType="ticket-cards"
        />
      </ExportRow>
    </>
  );
}

// ─── Pricing comparison table ────────────────────────────────────────────────

function PricingTable({ data }: { data: PricingTableData }) {
  const theme = useTheme();
  const allVendors = data.rows.length > 0 ? data.rows[0].values.map((v) => v.vendor) : [];

  // One column per vendor/kind, derived from the same list the table renders,
  // so the CSV columns can't drift from what is on screen.
  const csvColumns: CsvColumn<PricingTableData['rows'][number]>[] = [
    { header: 'Category', value: (r) => r.label },
    ...allVendors.map((vendor) => ({
      header: vendor,
      value: (r: PricingTableData['rows'][number]) =>
        r.values.find((v) => v.vendor === vendor)?.amount ?? '',
    })),
  ];

  return (
    <Box
      sx={{
        my: 1.5,
        borderRadius: 2,
        border: `1px solid ${alpha('#000', 0.07)}`,
        overflow: 'hidden',
        background: '#fff',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)}, ${alpha(theme.palette.primary.main, 0.08)})`,
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, fontSize: '0.75rem', color: theme.palette.primary.dark }}
          >
            {data.title}
          </Typography>
          <ExportCsvButton
            rows={data.rows}
            columns={csvColumns}
            title={data.title}
            blockType="pricing-table"
          />
        </Box>
      </Box>

      {/* Header row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `minmax(0, 1.4fr) ${allVendors.map(() => 'minmax(0, 1fr)').join(' ')}`,
          px: 2,
          py: 0.75,
          borderBottom: `1px solid ${alpha('#000', 0.05)}`,
          background: alpha('#000', 0.015),
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.68rem' }}
        >
          Component
        </Typography>
        {allVendors.map((vendor) => (
          <Typography
            key={vendor}
            variant="caption"
            sx={{
              fontWeight: 600,
              color: 'text.secondary',
              fontSize: '0.68rem',
              textAlign: 'right',
            }}
          >
            {vendor}
          </Typography>
        ))}
      </Box>

      {/* Data rows */}
      {data.rows.map((row, i) => (
        <Box
          key={row.label}
          sx={{
            display: 'grid',
            gridTemplateColumns: `minmax(0, 1.4fr) ${row.values.map(() => 'minmax(0, 1fr)').join(' ')}`,
            px: 2,
            py: 0.75,
            borderBottom: i < data.rows.length - 1 ? `1px solid ${alpha('#000', 0.04)}` : undefined,
            '&:hover': { background: alpha(theme.palette.primary.main, 0.02) },
          }}
        >
          <Typography variant="caption" sx={{ fontSize: '0.73rem', color: 'text.primary' }}>
            {row.label}
          </Typography>
          {row.values.map((val) => {
            const isHighest = Math.max(...row.values.map((v) => v.amount)) === val.amount;
            const isLowest = Math.min(...row.values.map((v) => v.amount)) === val.amount;
            return (
              <Box key={val.vendor} sx={{ textAlign: 'right' }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.73rem',
                    fontWeight: 600,
                    color:
                      row.values.length > 1
                        ? isHighest
                          ? theme.palette.error.main
                          : isLowest
                            ? theme.palette.success.main
                            : 'text.primary'
                        : 'text.primary',
                  }}
                >
                  {formatCurrency(val.amount)}
                </Typography>
                {val.invoiceCount != null && (
                  <Typography
                    variant="caption"
                    sx={{ fontSize: '0.6rem', color: 'text.disabled', display: 'block' }}
                  >
                    ({val.invoiceCount} inv.)
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      ))}

      {/* Portfolio average footer */}
      {data.portfolioAvg != null && (
        <Box
          sx={{
            px: 2,
            py: 0.75,
            borderTop: `1px solid ${alpha('#000', 0.06)}`,
            background: alpha(theme.palette.primary.main, 0.03),
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary' }}
          >
            Portfolio average
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, fontSize: '0.73rem', color: theme.palette.primary.dark }}
          >
            {formatCurrency(data.portfolioAvg)}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ─── Trend mini-chart ────────────────────────────────────────────────────────

export function TrendChart({ data }: { data: TrendChartData }) {
  const theme = useTheme();
  const lineColor = theme.palette.primary.main;
  // Match the rest of the chat surface — same `formatNumber` rounding so
  // tooltip values agree with table cells / KPI gauges.
  const valueSuffix = data.unit ?? '';

  const option: AreaLineChartOption = useMemo(
    () => ({
      aria: { enabled: true },
      backgroundColor: 'transparent',
      grid: { top: 8, right: 8, bottom: 8, left: 8, containLabel: true },
      legend: { show: false },
      xAxis: {
        type: 'category',
        data: data.points.map((p) => p.label),
        axisLine: { lineStyle: { color: alpha('#000', 0.1) } },
        axisTick: { lineStyle: { color: alpha('#000', 0.1) } },
        axisLabel: { color: alpha('#000', 0.55), fontSize: '0.68rem' },
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: alpha('#000', 0.06) } },
        // The tooltip shows precise values; the axis just needs to give the
        // reader a sense of scale. Keep two decimals so ratio metrics like
        // callback rate (1.12) don't round to 1.
        axisLabel: {
          color: alpha('#000', 0.55),
          fontSize: '0.68rem',
          formatter: (value: number) => `${formatNumber(value)}${valueSuffix}`,
        },
      },
      tooltip: {
        trigger: 'axis',
        // name is the category (e.g. "2025-11"); value is the metric.
        formatter: (params) => {
          const point = firstTooltipParam(params);
          return `<b>${point.name}</b><br/>${formatNumber(point.value as number)}${valueSuffix}`;
        },
      },
      series: [
        {
          type: 'line',
          name: data.title,
          smooth: true,
          // Belt-and-suspenders: the backend zero-fills missing buckets so we
          // shouldn't see nulls here, but connectNulls keeps the line
          // continuous if any slip through (e.g. a fresh metric the backfill
          // misses).
          connectNulls: true,
          data: data.points.map((p) => p.value),
          lineStyle: { color: lineColor, width: 2 },
          itemStyle: { color: '#fff', borderColor: lineColor, borderWidth: 2 },
          symbol: 'circle',
          symbolSize: 6,
          emphasis: { scale: 5 / 3 },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: alpha(lineColor, 0.25) },
              { offset: 1, color: alpha(lineColor, 0) },
            ]),
          },
        },
      ],
    }),
    [data, lineColor, valueSuffix],
  );

  return (
    <Box
      sx={{
        my: 1.5,
        borderRadius: 2,
        border: `1px solid ${alpha('#000', 0.07)}`,
        overflow: 'hidden',
        background: '#fff',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)}, ${alpha(theme.palette.primary.main, 0.08)})`,
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, fontSize: '0.75rem', color: theme.palette.primary.dark }}
          >
            {data.title}
          </Typography>
          <ExportCsvButton
            rows={data.points}
            columns={TREND_CSV_COLUMNS}
            title={data.title}
            blockType="trend-chart"
          />
        </Box>
      </Box>

      <Box sx={{ px: 1, pt: 0.5, pb: 0.5 }}>
        <ReactECharts echarts={echarts} option={option} style={{ height: 165, width: '100%' }} />
      </Box>
    </Box>
  );
}

// ─── Stat highlights ─────────────────────────────────────────────────────────

function StatHighlights({ stats }: { stats: StatHighlightData[] }) {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', gap: 1, my: 1.5, flexWrap: 'wrap' }}>
      {stats.map((stat) => {
        const color =
          stat.sentiment === 'positive'
            ? theme.palette.success.main
            : stat.sentiment === 'negative'
              ? theme.palette.error.main
              : theme.palette.primary.main;

        return (
          <Box
            key={stat.label}
            sx={{
              px: 2,
              py: 1,
              borderRadius: 2,
              background: alpha(color, 0.06),
              border: `1px solid ${alpha(color, 0.15)}`,
              minWidth: 120,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, color, fontSize: '1rem' }}>
              {stat.value}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>
              {stat.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Performance table ──────────────────────────────────────────────────────

export function PerformanceTable({ data }: { data: PerformanceTableData }) {
  const theme = useTheme();

  function formatCell(value: number | string | null, format?: string): string {
    if (value == null) return '—';
    if (typeof value === 'string') return value;
    if (format === 'percent') return `${formatNumber(value)}%`;
    if (format === 'hours') return `${formatNumber(value)}h`;
    return formatNumber(value);
  }

  // Raw values, not formatCell output: a spreadsheet should get 94, not "94%",
  // so the column stays numeric and sortable.
  const csvColumns: CsvColumn<PerformanceTableData['rows'][number]>[] = [
    { header: 'Property', value: (r) => r.label },
    ...data.columns.map((col) => ({
      header: col.label,
      value: (r: PerformanceTableData['rows'][number]) => r.values[col.key] ?? '',
    })),
  ];

  return (
    <Box
      sx={{
        my: 1.5,
        borderRadius: 2,
        border: `1px solid ${alpha('#000', 0.07)}`,
        overflow: 'hidden',
        background: '#fff',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          background: `linear-gradient(135deg, ${alpha('#4527A0', 0.04)}, ${alpha('#4527A0', 0.08)})`,
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#4527A0' }}
          >
            {data.title}
          </Typography>
          <ExportCsvButton
            rows={data.rows}
            columns={csvColumns}
            title={data.title}
            blockType="performance-table"
          />
        </Box>
      </Box>

      {/* Header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `minmax(0, 1.6fr) ${data.columns.map(() => 'minmax(0, 1fr)').join(' ')}`,
          px: 2,
          py: 0.75,
          borderBottom: `1px solid ${alpha('#000', 0.05)}`,
          background: alpha('#000', 0.015),
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.68rem' }}
        >
          &nbsp;
        </Typography>
        {data.columns.map((col) => (
          <Typography
            key={col.key}
            variant="caption"
            sx={{
              fontWeight: 600,
              color: 'text.secondary',
              fontSize: '0.68rem',
              textAlign: 'right',
            }}
          >
            {col.label}
          </Typography>
        ))}
      </Box>

      {/* Rows */}
      {data.rows.map((row, i) => (
        <Box
          key={row.label}
          sx={{
            display: 'grid',
            gridTemplateColumns: `minmax(0, 1.6fr) ${data.columns.map(() => 'minmax(0, 1fr)').join(' ')}`,
            px: 2,
            py: 0.75,
            borderBottom: i < data.rows.length - 1 ? `1px solid ${alpha('#000', 0.04)}` : undefined,
            '&:hover': { background: alpha('#4527A0', 0.02) },
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontSize: '0.73rem', color: 'text.primary', fontWeight: 500 }}
          >
            {row.label}
          </Typography>
          {data.columns.map((col) => {
            const val = row.values[col.key];
            const numVal = typeof val === 'number' ? val : null;
            // Color coding for performance metrics
            let color = 'text.primary';
            if (numVal != null && col.highlightBest) {
              const allVals = data.rows
                .map((r) => r.values[col.key])
                .filter((v): v is number => typeof v === 'number');
              const best =
                col.highlightBest === 'low' ? Math.min(...allVals) : Math.max(...allVals);
              const worst =
                col.highlightBest === 'low' ? Math.max(...allVals) : Math.min(...allVals);
              if (numVal === best) color = theme.palette.success.main;
              else if (numVal === worst) color = theme.palette.error.main;
            }
            return (
              <Typography
                key={col.key}
                variant="caption"
                sx={{ fontSize: '0.73rem', fontWeight: 600, textAlign: 'right', color }}
              >
                {formatCell(val, col.format)}
              </Typography>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}

// ─── KPI Gauge ──────────────────────────────────────────────────────────────

export function KPIGaugeSet({ gauges }: { gauges: KPIGaugeData[] }) {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', gap: 1.5, my: 1.5, flexWrap: 'wrap' }}>
      {gauges.map((gauge) => {
        // Treat null/non-finite values as "no data". Without this guard
        // `null <= target` silently returns true (passing → green),
        // `null / target` is NaN, and formatNumber(null) renders the
        // literal string "null" or "null%". See Sentry LIFTAI-API-2N4
        // adjacent: properties with no callback/maintenance data sent
        // null gauges through unchanged.
        const hasValue = gauge.value != null && Number.isFinite(gauge.value);
        const passing =
          hasValue &&
          (gauge.lowerIsBetter
            ? (gauge.value as number) <= gauge.target
            : (gauge.value as number) >= gauge.target);
        const pct = !hasValue
          ? 0
          : gauge.lowerIsBetter
            ? Math.max(0, Math.min(100, (1 - (gauge.value as number) / (gauge.target * 2)) * 100))
            : Math.max(0, Math.min(100, ((gauge.value as number) / gauge.target) * 100));
        const color = !hasValue
          ? theme.palette.text.disabled
          : passing
            ? theme.palette.success.main
            : theme.palette.error.main;
        const fmtVal = !hasValue
          ? '—'
          : gauge.format === 'percent'
            ? `${formatNumber(gauge.value as number)}%`
            : formatNumber(gauge.value as number);
        const fmtTarget =
          gauge.format === 'percent'
            ? `${formatNumber(gauge.target)}%`
            : formatNumber(gauge.target);

        return (
          <Box
            key={gauge.label}
            sx={{
              flex: '1 1 140px',
              maxWidth: 200,
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${alpha(color, 0.2)}`,
              background: alpha(color, 0.04),
              textAlign: 'center',
            }}
          >
            {/* Mini progress bar */}
            <Box
              sx={{
                width: '100%',
                height: 6,
                borderRadius: 3,
                background: alpha('#000', 0.06),
                mb: 1,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: `${pct}%`,
                  height: '100%',
                  borderRadius: 3,
                  background: color,
                  transition: 'width 0.6s ease',
                }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color, fontSize: '1.1rem', lineHeight: 1 }}
            >
              {fmtVal}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontSize: '0.68rem', display: 'block', mt: 0.25 }}
            >
              {gauge.label}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.62rem' }}>
              Target: {gauge.lowerIsBetter ? '< ' : '≥ '}
              {fmtTarget}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Block renderer ──────────────────────────────────────────────────────────

const REVIEW_RULE_CATEGORY_LABELS: Record<ReviewRuleCategory, string> = {
  'review-rule': 'Review Rule',
  'included-service': 'Included Service',
  'excluded-service': 'Excluded Service',
  'miscellaneous-charge': 'Miscellaneous Charge',
  'other-term': 'Other Term',
};

type AddState = 'idle' | 'saving' | 'added' | 'error';

function SuggestedRuleRow({
  rule,
  contractId,
  ruleIndex,
  ruleCountInCard,
  hadConflicts,
}: {
  rule: SuggestedReviewRule;
  contractId: string;
  ruleIndex: number;
  ruleCountInCard: number;
  hadConflicts: boolean;
}) {
  const [addState, setAddState] = useState<AddState>('idle');
  const [errorDetail, setErrorDetail] = useState<string>();
  const { track } = useAnalytics();
  const { sessionId, invoiceId } = useContext(ChatAttributionContext);

  const handleAdd = async () => {
    setAddState('saving');
    setErrorDetail(undefined);
    try {
      await contractsReviewRulesCreate(contractId, {
        reviewRule: rule.reviewRule,
        category: rule.category,
      });
      setAddState('added');
      track('rule_suggestion_rule_added', {
        contract_id: contractId,
        category: rule.category,
        rule_count_in_card: ruleCountInCard,
        rule_index: ruleIndex,
        had_conflicts: hadConflicts,
        ...(invoiceId ? { invoice_id: invoiceId } : {}),
        ...(sessionId ? { session_id: sessionId } : {}),
      });
    } catch (e) {
      setAddState('error');
      // A refusal carries its reason (e.g. a 403 when the contract covers a
      // property outside the caller's accounts); show it instead of "try again".
      setErrorDetail(liftAPIClientErrorDetail(e));
      // Bounded error code for analytics (mirrors dispute_email_send_failed).
      const errorCode: `http_${number}` | 'unknown_error' =
        e instanceof LiftAPIClientError ? `http_${e.context.status}` : 'unknown_error';
      track('rule_suggestion_add_failed', {
        contract_id: contractId,
        category: rule.category,
        error_code: errorCode,
      });
    }
  };

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${alpha('#000', 0.07)}`,
        background: '#fff',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', flex: 1 }}>
          {rule.reviewRule}
        </Typography>
        <Chip
          label={REVIEW_RULE_CATEGORY_LABELS[rule.category]}
          size="small"
          sx={{ height: 20, fontSize: '0.65rem' }}
        />
      </Box>
      {addState === 'added' ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
          <CheckCircleRoundedIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption" sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
            Added to contract
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={addState === 'saving'}
            startIcon={
              addState === 'saving' ? (
                <CircularProgress size={12} />
              ) : (
                <AddRoundedIcon sx={{ fontSize: 16 }} />
              )
            }
            onClick={handleAdd}
            sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.25 }}
          >
            Add rule
          </Button>
          {addState === 'error' ? (
            <Typography variant="caption" sx={{ color: 'error.main', fontSize: '0.7rem' }}>
              {errorDetail ? `Couldn't add. ${errorDetail}` : "Couldn't add — try again."}
            </Typography>
          ) : null}
        </Box>
      )}
    </Box>
  );
}

export function RuleSuggestionsCard({ data }: { data: RuleSuggestionsCardData }) {
  const theme = useTheme();
  const { track } = useAnalytics();
  const { sessionId } = useContext(ChatAttributionContext);
  const hasConflicts = data.conflictingRuleIds.length > 0;
  const ruleCount = data.suggestedRules.length;

  // Fire once per card instance, only when it actually paints a non-empty card
  // (chat_tool_result_rendered fires on tool return, which can be an empty
  // card) — gives the true card->add denominator for the adoption funnel.
  const shownRef = useRef(false);
  useEffect(() => {
    if (shownRef.current || ruleCount === 0) return;
    shownRef.current = true;
    track('rule_suggestion_card_shown', {
      contract_id: data.contractId,
      suggested_rule_count: ruleCount,
      conflicting_rule_count: data.conflictingRuleIds.length,
      ...(sessionId ? { session_id: sessionId } : {}),
    });
  }, [data.contractId, data.conflictingRuleIds.length, ruleCount, sessionId, track]);

  if (ruleCount === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <RuleRoundedIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>
          Suggested review rules
        </Typography>
      </Box>
      {data.suggestedRules.map((rule, i) => (
        <SuggestedRuleRow
          key={`${rule.reviewRule}-${i}`}
          rule={rule}
          contractId={data.contractId}
          ruleIndex={i}
          ruleCountInCard={ruleCount}
          hadConflicts={hasConflicts}
        />
      ))}
      {hasConflicts ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.75,
            p: 1.25,
            borderRadius: 2,
            background: alpha(theme.palette.warning.main, 0.08),
          }}
        >
          <WarningAmberRoundedIcon
            sx={{ fontSize: 16, color: theme.palette.warning.main, mt: 0.1 }}
          />
          <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
            {data.conflictingRuleIds.length === 1
              ? '1 existing rule on this contract may conflict with your decision — review it on the contract.'
              : `${data.conflictingRuleIds.length} existing rules on this contract may conflict with your decision — review them on the contract.`}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

export function RenderContentBlock({ block }: { block: ContentBlock }) {
  const { sessionId } = useContext(ChatAttributionContext);
  switch (block.type) {
    case 'knowledge-draft':
      return <KnowledgeDraftCard data={block.data} sessionId={sessionId} />;
    case 'knowledge-results':
      return <KnowledgeResultsCard data={block.data} />;
    case 'knowledge-target-choice':
      return <KnowledgeTargetChoiceCard data={block.data} sessionId={sessionId} />;
    case 'knowledge-content-prompt':
      return <KnowledgeContentPromptCard data={block.data} />;
    case 'rule-suggestions':
      return <RuleSuggestionsCard data={block.data} />;
    case 'text':
      // ChatMarkdown renders untrusted AI/tool output: raw HTML is dropped and
      // only safe URL schemes survive — see ChatMarkdown.tsx.
      return <ChatMarkdown>{block.content}</ChatMarkdown>;
    case 'invoice-cards':
      return <InvoiceCardList invoices={block.invoices} />;
    case 'property-cards':
      return <PropertyCardList properties={block.properties} />;
    case 'vendor-cards':
      return <VendorCardList vendors={block.vendors} />;
    case 'unit-cards':
      return <UnitCardList units={block.units} />;
    case 'ticket-cards':
      return <TicketCardList tickets={block.tickets} />;
    case 'pricing-table':
      return <PricingTable data={block.data} />;
    case 'performance-table':
      return <PerformanceTable data={block.data} />;
    case 'kpi-gauge':
      return <KPIGaugeSet gauges={block.gauges} />;
    case 'trend-chart':
      return <TrendChart data={block.data} />;
    case 'stat-highlights':
      return <StatHighlights stats={block.stats} />;
    case 'compliance-cards':
      return <ComplianceCardList data={block.data} />;
    default:
      return null;
  }
}
